import { call, put, takeEvery, all, select } from 'redux-saga/effects';

import { toQueryString } from 'utils/UrlUtil';

import { isChinaUser } from 'utils/ChinaUtil';

import { getDistanceType } from 'utils/pacePro/PaceProUtil';

import {
    FETCH_PACE_PRO_BANDS,
    FETCH_PACE_PRO_BANDS_SUCCESS,
    FETCH_PACE_PRO_BANDS_FAIL,
    FETCH_PACE_PRO_BAND,
    FETCH_PACE_PRO_BAND_SUCCESS,
    FETCH_PACE_PRO_BAND_FAIL,
    UPDATE_PACE_PRO_BAND,
    UPDATE_PACE_PRO_BAND_SUCCESS,
    UPDATE_PACE_PRO_BAND_FAIL,
    SAVE_PACE_PRO_BAND,
    SAVE_PACE_PRO_BAND_SUCCESS,
    SAVE_PACE_PRO_BAND_FAIL,
    FETCH_PACE_PRO_BANDS_SUMMARY,
    FETCH_PACE_PRO_BANDS_SUMMARY_SUCCESS,
    FETCH_PACE_PRO_BANDS_SUMMARY_FAIL,
    SEND_PACEPRO_TO_DEVICE,
    SEND_PACEPRO_TO_DEVICE_SUCCESS,
    SEND_PACEPRO_TO_DEVICE_ERROR,
    DELETE_PACEPRO,
    DELETE_PACEPRO_SUCCESS,
    DELETE_PACEPRO_FAIL,
    FETCH_PACE_PRO_BAND_COURSE
} from '../actions/paceProBandActions';
import { chinaInterpreterSetPacePro } from 'actions/chinaInterpreterActions';
import { selectChinaInterpreterPaceProCreate } from 'reducers/chinaInterpreterReducer/chinaInterpreterReducer';

import { SAVE_STATES } from 'reducers/pacePro/paceProCreate';

import {
    FETCH_COURSE_DETAIL_SUCCESS,
    FETCH_COURSE_DETAIL_FAIL,
    INITIALIZE_PACEPRO_CREATE
} from '../actions/ActionTypes';

import { gcApi } from '../config/axios';

export function* fetchPaceProBandsSaga(action) {
    try {
        const response = yield call(gcApi.get, `/course-service/pacebands`);

        yield put({
            type: FETCH_PACE_PRO_BANDS_SUCCESS,
            paceProBands: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_PACE_PRO_BANDS_FAIL,
            paceProBands: []
        });
    }
}

export function* fetchPaceProBandSaga(action) {
    try {
        const response = yield call(gcApi.get, `/course-service/pacebands/${action.paceBandId}`);

        yield put({
            type: FETCH_PACE_PRO_BAND_SUCCESS,
            paceProBand: response.data
        });

        const totalDistance = response.data.paceBandSummary.totalDistance;

        let pacePro = {
            course: response.data.paceBandSummary.courseId,
            customDistance: response.data.paceBandSummary.totalDistance,
            goalTime: response.data.paceBandSummary.goalTime,
            splits: response.data.paceBandSummary.splitType,
            pacingStrategy: response.data.paceBandSummary.pacingStrategy,
            hillEffort: response.data.paceBandSummary.uphillEffort,
            elevationSegmentMinLength: response.data.paceBandSummary.elevationSegmentMinLength,
            id: response.data.paceBandSummary.paceBandPk,
            name: response.data.paceBandSummary.name,
            distance: getDistanceType(totalDistance, response.data.paceBandSummary.courseId),
            manualPaceEdit: response.data.paceBandSummary.manualPaceEdit,
            saveState: SAVE_STATES.SAVED,
            paceIntervals: response.data.paceBandSplits,
            customDistanceUnit: response.data.paceBandSummary.distanceUnit
        };

        if (response.data.paceBandSummary.courseId) {
            // grab the course
            let queryString = '';

            let courseResponse = null;

            if (isChinaUser()) {
                queryString = toQueryString({
                    targetCoordinateSystem: 'BD09'
                });
                const responses = yield all([
                    call(gcApi.get, `/course-service/course/${response.data.paceBandSummary.courseId}${queryString}`),
                    call(gcApi.get, `/course-service/course/${response.data.paceBandSummary.courseId}`)
                ]);

                /**
                 * china mode, we use shifted response
                 */
                courseResponse = responses[0];

                /**
                 * we need original geoPoints for calculation
                 */
                const originalCourseResponse = responses[1];
                yield put(
                    chinaInterpreterSetPacePro({
                        geoPoints: originalCourseResponse.data?.geoPoints
                    })
                );
            } else {
                courseResponse = yield call(
                    gcApi.get,
                    `/course-service/course/${response.data.paceBandSummary.courseId}`
                );
            }

            yield put({
                type: FETCH_COURSE_DETAIL_SUCCESS,
                course: courseResponse.data
            });

            pacePro.customDistance = courseResponse.data.distanceMeter;
        }

        yield put({
            type: INITIALIZE_PACEPRO_CREATE,
            paceProCreate: pacePro
        });
    } catch (e) {
        yield put({
            type: FETCH_PACE_PRO_BAND_FAIL,
            paceProBand: {}
        });
    }
}

export function* fetchCourseDetailForPacePro(action) {
    try {
        let queryString = '';
        let courseResponse;
        if (isChinaUser()) {
            queryString = toQueryString({
                targetCoordinateSystem: 'BD09'
            });
            const responses = yield all([
                call(gcApi.get, `/course-service/course/${action.courseId}${queryString}`),
                call(gcApi.get, `/course-service/course/${action.courseId}`)
            ]);

            /**
             * china mode, we use shifted response
             */
            courseResponse = responses[0];

            /**
             * we need original geoPoints for calculation
             */
            const originalCourseResponse = responses[1];
            yield put(
                chinaInterpreterSetPacePro({
                    geoPoints: originalCourseResponse.data?.geoPoints
                })
            );
        } else {
            courseResponse = yield call(gcApi.get, `/course-service/course/${action.courseId}`);
        }

        yield put({
            type: FETCH_COURSE_DETAIL_SUCCESS,
            course: courseResponse.data
        });

        yield put({
            type: INITIALIZE_PACEPRO_CREATE,
            paceProCreate: { customDistance: courseResponse.data.distanceMeter }
        });
    } catch (e) {
        yield put({
            type: FETCH_COURSE_DETAIL_FAIL,
            course: {}
        });
    }
}

// Rounds the cumulativeTime to 3 decimal places for API to accept Edited Splits
function roundPaceProCumulativeTime(paceBand) {
    let newPaceBand = JSON.parse(JSON.stringify(paceBand));
    newPaceBand.paceBandSplits.forEach((splits, i) => {
        let cumulativeTimeFromStart = Number(splits.cumulativeTimeFromStart).toFixed(3);
        newPaceBand.paceBandSplits[i].cumulativeTimeFromStart = parseFloat(cumulativeTimeFromStart);
    });
    return newPaceBand;
}

export function* savePaceProBandSaga(action) {
    try {
        let paceProBand = roundPaceProCumulativeTime(action.paceProBand);
        // if the user does not own the course and the course does not have a shareable event on it, we must copy it and use the copy
        // pacebands cannot be associated with unowned courses
        if (action.courseNotOwned && !action.hasShareableEvent) {
            const courseResponse = yield call(
                gcApi.get,
                `/course-service/course/copyExistingCourse/${action.paceProBand.paceBandSummary.courseId}`
            );

            paceProBand.paceBandSummary.courseId = courseResponse.data.courseId;
        }

        if (isChinaUser()) {
            const chinaInterpreterPaceProCreate = yield select(selectChinaInterpreterPaceProCreate);
            if (chinaInterpreterPaceProCreate?.paceIntervals) {
                paceProBand.paceBandSplits = chinaInterpreterPaceProCreate.paceIntervals;
            }
        }

        const response = yield call(gcApi.post, `/course-service/pacebands`, paceProBand);

        yield put({
            type: SAVE_PACE_PRO_BAND_SUCCESS,
            paceProBand: response.data
        });
    } catch (e) {
        yield put({
            type: SAVE_PACE_PRO_BAND_FAIL
        });
    }
}

export function* updatePaceProBandSaga(action) {
    try {
        let paceProBand = roundPaceProCumulativeTime(action.paceProBand);
        const response = yield call(gcApi.put, `/course-service/pacebands/${action.paceBandId}`, paceProBand);

        yield put({
            type: UPDATE_PACE_PRO_BAND_SUCCESS,
            paceProBand: response.data
        });
    } catch (e) {
        yield put({
            type: UPDATE_PACE_PRO_BAND_FAIL
        });
    }
}

export function* fetchPaceProBandSummarySaga(action) {
    try {
        const queryString = toQueryString({
            courseId: action.courseId
        });

        const response = yield call(gcApi.get, `/course-service/pacebands/summaries${queryString}`);

        yield put({
            type: FETCH_PACE_PRO_BANDS_SUMMARY_SUCCESS,
            paceProBandSummaries: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_PACE_PRO_BANDS_SUMMARY_FAIL,
            paceProBandSummaries: []
        });
    }
}

export function* sendPaceProBandToDevice(action) {
    const { deviceId, paceBandId } = action.payload;

    try {
        const response = yield call(gcApi.post, `/course-service/pacebands/${paceBandId}/device/${deviceId}`, {
            ...action.payload
        });
        yield put({
            type: SEND_PACEPRO_TO_DEVICE_SUCCESS,
            response
        });
    } catch (e) {
        yield put({
            type: SEND_PACEPRO_TO_DEVICE_ERROR
        });
    }
}

export function* deletePaceProSaga(action) {
    const { paceBandId } = action;

    try {
        const response = yield call(gcApi.delete, `/course-service/pacebands/${paceBandId}`);

        if (response.status === 204) {
            yield put({
                type: DELETE_PACEPRO_SUCCESS,
                paceBandId: paceBandId
            });
        }
    } catch (ex) {
        yield put({
            type: DELETE_PACEPRO_FAIL,
            paceBandId: paceBandId
        });
    }
}

export const paceProBand = [
    takeEvery(FETCH_PACE_PRO_BANDS, fetchPaceProBandsSaga),
    takeEvery(FETCH_PACE_PRO_BAND, fetchPaceProBandSaga),
    takeEvery(SAVE_PACE_PRO_BAND, savePaceProBandSaga),
    takeEvery(UPDATE_PACE_PRO_BAND, updatePaceProBandSaga),
    takeEvery(FETCH_PACE_PRO_BANDS_SUMMARY, fetchPaceProBandSummarySaga),
    takeEvery(SEND_PACEPRO_TO_DEVICE, sendPaceProBandToDevice),
    takeEvery(DELETE_PACEPRO, deletePaceProSaga),
    takeEvery(FETCH_PACE_PRO_BAND_COURSE, fetchCourseDetailForPacePro)
];
